import React, { useEffect, useState } from "react";
import {
  PieChart,
  Pie,
  Cell,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";

const fetchWithCheck = async (url) => {
  const response = await fetch(url);
  const contentType = response.headers.get("Content-Type");
  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(
      `HTTP error! status: ${response.status}, message: ${errorText}`
    );
  }
  if (!contentType || !contentType.includes("application/json")) {
    const errorText = await response.text();
    throw new Error(
      `Invalid content type: ${contentType}, message: ${errorText}`
    );
  }
  return response.json();
};

const formatCurrency = (value) => {
  if (isNaN(value)) return "$0.00";
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 2,
  }).format(value);
};

const formatNumber = (value) => {
  if (isNaN(value)) return "0.00";
  return new Intl.NumberFormat("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value);
};

function App() {
  const [delegations, setDelegations] = useState([]);
  const [decentralizationData, setDecentralizationData] = useState(null);
  const [infraDeals, setInfraDeals] = useState([]);
  const [oldInfraCosts, setOldInfraCosts] = useState([]);
  const [newInfraCosts, setNewInfraCosts] = useState([]);
  const [fetchErrors, setFetchErrors] = useState([]);
  const [totalDelegated, setTotalDelegated] = useState(0);
  const [stakeEfficiencyData, setStakeEfficiencyData] = useState([]);

  const API_BASE_URL = "https://api.slabs.secretnodes.com";

  useEffect(() => {
    async function fetchData() {
      const errors = [];
      try {
        const delegationResponse = await fetchWithCheck(
          `${API_BASE_URL}/slabs-delegations`
        );
        setDelegations(delegationResponse);

        const total = delegationResponse.reduce(
          (acc, curr) => acc + parseFloat(curr.slabs_delegation_total || 0),
          0
        );
        setTotalDelegated(total);

        const decentralizationResponse = await fetchWithCheck(
          `${API_BASE_URL}/decentralization`
        );
        setDecentralizationData(decentralizationResponse);

        const stakesToDecentralize = parseFloat(
          decentralizationResponse["Stakes to decentralize"] || 0
        );

        const remainingStakes = total - stakesToDecentralize;

        setStakeEfficiencyData([
          { name: "Stakes to Decentralize", value: stakesToDecentralize },
          { name: "Remaining Stakes", value: remainingStakes },
        ]);
      } catch (error) {
        console.error("Delegations or Decentralization fetch error:", error);
        errors.push("Delegations or Decentralization endpoint failed.");
      }

      try {
        const infraDealsResponse = await fetchWithCheck(
          `${API_BASE_URL}/infra-deal-savings`
        );
        setInfraDeals(infraDealsResponse.deals || []);
      } catch (error) {
        console.error("Infra Deal Savings fetch error:", error);
        errors.push("Infra deal savings endpoint failed.");
      }

      try {
        const oldInfraCostsResponse = await fetchWithCheck(
          `${API_BASE_URL}/old-infra-costs`
        );
        setOldInfraCosts(oldInfraCostsResponse.costs || []);
      } catch (error) {
        console.error("Old Infra Costs fetch error:", error);
        errors.push("Old infra costs endpoint failed.");
      }

      try {
        const newInfraCostsResponse = await fetchWithCheck(
          `${API_BASE_URL}/new-infra-costs`
        );
        setNewInfraCosts(newInfraCostsResponse.costs || []);
      } catch (error) {
        console.error("New Infra Costs fetch error:", error);
        errors.push("New infra costs endpoint failed.");
      }

      setFetchErrors(errors);
    }

    fetchData();
  }, [API_BASE_URL]);

  const calculateTotalCost = (costs) =>
    costs.reduce((sum, item) => sum + parseFloat(item.cost || 0), 0).toFixed(2);

  const pieColors = [
    "#0088FE",
    "#00C49F",
    "#FFBB28",
    "#FF8042",
    "#AA336A",
    "#33AA99",
    "#AA6633",
  ];

  return (
    <div style={containerStyle}>
      <header style={headerStyle}>
        <h1 style={titleStyle}>D.O.S.E</h1>
        <p style={subtitleStyle}>
          <strong>Department of Secret Efficiency</strong> &ndash; Unofficial
          Initiative &ndash;
          <a href="https://t.me/+q-QbVI0-QWw0NWMx" style={linkStyle}>
            Request to Join
          </a>
        </p>
      </header>

      {fetchErrors.length > 0 && (
        <div style={errorStyle}>
          <h3>Errors:</h3>
          <ul>
            {fetchErrors.map((error, idx) => (
              <li key={idx}>{error}</li>
            ))}
          </ul>
        </div>
      )}

      <section style={sectionStyle}>
        <h2 style={sectionTitleStyle}>Stake Efficiency</h2>
        <p>
          <strong>Total Delegated Stakes:</strong> {formatNumber(totalDelegated)} SCRT
        </p>
        <p>
          <strong>Stakes to Decentralize:</strong>{" "}
          {formatNumber(
            parseFloat(decentralizationData?.["Stakes to decentralize"] || 0)
          )} SCRT
        </p>
        <p style={noteStyle}>
          *Curated up-to-date delegations vs delegations to those who have left
          the network or stopped contributions.
        </p>
        <div style={chartContainerStyle}>
          <ResponsiveContainer width="100%" height="100%">
            <PieChart>
              <Pie
                data={stakeEfficiencyData}
                dataKey="value"
                nameKey="name"
                cx="50%"
                cy="50%"
                outerRadius={100}
                label
              >
                {stakeEfficiencyData.map((entry, index) => (
                  <Cell
                    key={`cell-${index}`}
                    fill={pieColors[index % pieColors.length]}
                  />
                ))}
              </Pie>
              <Tooltip formatter={(value) => `${formatNumber(value)} SCRT`} />
              <Legend />
            </PieChart>
          </ResponsiveContainer>
        </div>
      </section>

      <section style={sectionStyle}>
        <h2 style={sectionTitleStyle}>Infra Deal Savings</h2>
        {infraDeals.length > 0 ? (
          <table style={tableStyle}>
            <thead>
              <tr style={headerRowStyle}>
                <th style={cellStyle}>Deal</th>
                <th style={cellStyle}>Source</th>
                <th style={cellStyle}>Cost</th>
              </tr>
            </thead>
            <tbody>
              {infraDeals.map((deal, idx) => (
                <tr key={idx}>
                  <td style={cellStyle}>{deal.deal}</td>
                  <td style={cellStyle}>{deal.nodeCount}</td>
                  <td style={cellStyle}>{formatCurrency(deal.cost)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <p>No Infra Deal Savings data available.</p>
        )}
      </section>

      <section style={sectionStyle}>
        <h2 style={sectionTitleStyle}>Old Infrastructure Costs</h2>
        {oldInfraCosts.length > 0 ? (
          <table style={tableStyle}>
            <thead>
              <tr style={headerRowStyle}>
                <th style={cellStyle}>Category</th>
                <th style={cellStyle}>Source</th>
                <th style={cellStyle}>Cost</th>
              </tr>
            </thead>
            <tbody>
              {oldInfraCosts.map((cost, idx) => (
                <tr key={idx}>
                  <td style={cellStyle}>{cost.category}</td>
                  <td style={cellStyle}>{cost.source}</td>
                  <td style={cellStyle}>{formatCurrency(cost.cost)}</td>
                </tr>
              ))}
              <tr style={totalRowStyle}>
                <td colSpan="2" style={cellStyle}><strong>Total</strong></td>
                <td style={cellStyle}>{formatCurrency(calculateTotalCost(oldInfraCosts))}</td>
              </tr>
            </tbody>
          </table>
        ) : (
          <p>No Old Infrastructure Costs data available.</p>
        )}
      </section>

      <section style={sectionStyle}>
        <h2 style={sectionTitleStyle}>New Infrastructure Costs</h2>
        {newInfraCosts.length > 0 ? (
          <table style={tableStyle}>
            <thead>
              <tr style={headerRowStyle}>
                <th style={cellStyle}>Category</th>
                <th style={cellStyle}>Source</th>
                <th style={cellStyle}>Cost</th>
              </tr>
            </thead>
            <tbody>
              {newInfraCosts.map((cost, idx) => (
                <tr key={idx}>
                  <td style={cellStyle}>{cost.category}</td>
                  <td style={cellStyle}>{cost.source}</td>
                  <td style={cellStyle}>{formatCurrency(cost.cost)}</td>
                </tr>
              ))}
              <tr style={totalRowStyle}>
                <td colSpan="2" style={cellStyle}><strong>Total</strong></td>
                <td style={cellStyle}>{formatCurrency(calculateTotalCost(newInfraCosts))}</td>
              </tr>
            </tbody>
          </table>
        ) : (
          <p>No New Infrastructure Costs data available.</p>
        )}
      </section>

      <section style={sectionStyle}>
        <h2 style={sectionTitleStyle}>Curated Delegations Overview</h2>
        <div style={{ overflowX: "auto" }}>
          <table style={tableStyle}>
            <thead>
              <tr style={headerRowStyle}>
                <th style={cellStyle}>Validator Moniker</th>
                <th style={cellStyle}>Secret Valoper Address</th>
                <th style={cellStyle}>Total Delegation (SCRT)</th>
              </tr>
            </thead>
            <tbody>
              {delegations.length > 0 ? (
                delegations.map((item, idx) => (
                  <tr key={idx}>
                    <td style={cellStyle}>{item.validator_moniker || "Unknown"}</td>
                    <td style={cellStyle}>{item.validator_secretvaloper_address}</td>
                    <td style={cellStyle}>{formatNumber(parseFloat(item.slabs_delegation_total || 0))} SCRT</td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="3" style={cellStyle}>No delegations data available.</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
}

const containerStyle = {
  background: "linear-gradient(135deg, #1F1C2C, #928DAB)",
  color: "#E0E0E0",
  padding: "2rem",
  fontFamily: "'Roboto', sans-serif",
  minHeight: "100vh",
};

const headerStyle = {
  textAlign: "center",
  marginBottom: "2rem",
  padding: "1rem 0",
  borderBottom: "2px solid #F72585",
};

const titleStyle = {
  color: "#F72585",
  fontSize: "2.5rem",
  marginBottom: "0.5rem",
  letterSpacing: "2px",
};

const subtitleStyle = {
  fontSize: "1rem",
  color: "#CCCCCC",
};

const linkStyle = {
  color: "#F72585",
  textDecoration: "none",
  marginLeft: "0.5rem",
};

const errorStyle = {
  backgroundColor: "#ffdddd",
  borderLeft: "6px solid #f44336",
  padding: "1rem",
  marginBottom: "2rem",
  borderRadius: "4px",
};

const sectionStyle = {
  backgroundColor: "rgba(0, 0, 0, 0.4)",
  padding: "1.5rem",
  borderRadius: "8px",
  marginBottom: "2rem",
  boxShadow: "0 4px 8px rgba(0, 0, 0, 0.3)",
};

const sectionTitleStyle = {
  borderBottom: "1px solid #444",
  paddingBottom: "0.5rem",
  marginBottom: "1rem",
};

const noteStyle = {
  fontStyle: "italic",
  marginBottom: "1rem",
  color: "#CCCCCC",
};

const tableStyle = {
  width: "100%",
  borderCollapse: "collapse",
  backgroundColor: "#16213E",
  borderRadius: "8px",
  overflow: "hidden",
  boxShadow: "0 4px 8px rgba(0, 0, 0, 0.2)",
};

const headerRowStyle = {
  backgroundColor: "#0f3460",
};

const cellStyle = {
  border: "1px solid #F72585",
  padding: "12px 8px",
  textAlign: "center",
};

const totalRowStyle = {
  fontWeight: "bold",
  backgroundColor: "#1f4068",
};

const chartContainerStyle = {
  width: "100%",
  height: "300px",
  marginTop: "1rem",
  backgroundColor: "#16213E",
  borderRadius: "8px",
  padding: "1rem",
};

export default App;
